// Tickets: a project's board (columns = statuses) and list (grouped by section), the ticket panel, and board settings.
// Kept deliberately quiet: cards show only what helps you pick the next thing to do; everything else is in the panel.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCorners, useDroppable, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { del, get, patch, post, put } from '../api';
import { useAuth } from '../auth';
import { Icon, Menu, Modal, ago, formatDate, initials, useConfirm, useToast } from '../ui';
import { AttachmentList, RichTextarea, type RichTextareaHandle, type Uploaded } from './RichText';
import { Frame } from './Designs';
import type { Board, Filters, Person, Project, SavedView, Ticket, ViewConfig, TicketComment, TicketComments, TicketDesign, TicketDesigns, TicketDetail, TicketKind, TicketPriority, TicketSection, TicketStatus } from '../types';

const PRIORITY_LABEL: Record<TicketPriority, string> = { none: 'No priority', low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const KIND_LABEL: Record<TicketKind, string> = { task: 'Task', bug: 'Bug', feature: 'Feature', question: 'Question' };
const RECENT_DONE_DAYS = 14;

const today = () => new Date().toISOString().slice(0, 10);
const shortDate = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
const rand = (cents: number) => `R ${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const storage = { get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } } };

export function useBoard(projectId: string) {
  return useQuery({ queryKey: ['board', projectId], queryFn: () => get<Board>(`/api/projects/${projectId}/tickets`) });
}

// ---------- small pieces ----------

function Avatars({ people, max = 3 }: { people: Person[]; max?: number }) {
  if (!people.length) return null;
  return (
    <span className="avatars" title={people.map((p) => p.name).join(', ')}>
      {people.slice(0, max).map((p) => <span key={p.id} className={`avatar xs ${p.is_staff ? 'staff' : ''}`}>{initials(p.name)}</span>)}
      {people.length > max && <span className="avatar xs more">+{people.length - max}</span>}
    </span>
  );
}

function Due({ on, done }: { on: string | null; done: boolean }) {
  if (!on) return null;
  const late = !done && on < today();
  return <span className={`due ${late ? 'late' : ''}`} title={late ? 'Overdue' : 'Due'}><Icon name="calendar" className="ico xs" />{shortDate(on)}</span>;
}

const PriorityMark = ({ p }: { p: TicketPriority }) => p === 'none' ? null : <span className={`prio prio-${p}`} title={`${PRIORITY_LABEL[p]} priority`}>{PRIORITY_LABEL[p]}</span>;

function TicketCard({ t, done, onOpen, dragging }: { t: Ticket; done: boolean; onOpen?: () => void; dragging?: boolean }) {
  return (
    <div className={`tcard ${dragging ? 'dragging' : ''} ${t.internal ? 'internal' : ''}`} onClick={onOpen} role="button" tabIndex={-1}>
      <div className="tcard-title">
        {t.kind === 'bug' && <Icon name="bug" className="ico xs bug" />}
        <span>{t.title}</span>
      </div>
      <div className="tcard-meta">
        <span className="num">#{t.number}</span>
        <PriorityMark p={t.priority} />
        <Due on={t.due_on} done={done} />
        {t.internal && <span className="tag-internal" title="Blue Market only">Internal</span>}
        {t.comment_count > 0 && <span className="ccount" title={`${t.comment_count} comment${t.comment_count === 1 ? '' : 's'}`}>💬 {t.comment_count}</span>}
        <span className="grow" />
        <Avatars people={t.assignees} />
      </div>
    </div>
  );
}

function SortableTicket({ t, done, onOpen, disabled, children }: { t: Ticket; done: boolean; onOpen: () => void; disabled: boolean; children?: (t: Ticket) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id, disabled });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }} {...attributes} {...listeners}
         onKeyDown={(e) => { listeners?.onKeyDown?.(e); if (e.key === 'Enter') onOpen(); }}>
      {children ? children(t) : <TicketCard t={t} done={done} onOpen={onOpen} />}
    </div>
  );
}

function DropZone({ id, className, children }: { id: string; className?: string; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} className={`${className ?? ''} ${isOver ? 'over' : ''}`}>{children}</div>;
}

// ---------- the tab ----------

type View = 'board' | 'list';

export function TicketsTab({ project }: { project: Project }) {
  const { me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const { data } = useBoard(project.id);
  // Everything that shapes what you see, in one place: it's what a saved view stores.
  const adhocKey = `bmp.tickets.${project.id}`;
  const { data: views = [] } = useQuery({ queryKey: ['views', project.id], queryFn: () => get<SavedView[]>(`/api/projects/${project.id}/views`) });
  const viewId = search.get('view');
  const selectedView = views.find((v) => v.id === viewId) ?? null;
  const [config, setConfigState] = useState<ViewConfig>(() => {
    try { return normalise(JSON.parse(storage.get(adhocKey) || 'null') ?? { view: storage.get('bmp.tickets.view') || 'board' }); } catch { return normalise({}); }
  });
  const loadedView = useRef<string | null>(null);
  useEffect(() => {            // opening a saved view (from the menu or a link) loads its settings
    if (selectedView && loadedView.current !== selectedView.id) { loadedView.current = selectedView.id; setConfigState(normalise(selectedView.config)); }
    if (!viewId) loadedView.current = null;
  }, [selectedView, viewId]);
  const setConfig = (patch: Partial<ViewConfig>) => setConfigState((c) => {
    const next = normalise({ ...c, ...patch });
    if (!viewId) storage.set(adhocKey, JSON.stringify(next));
    storage.set('bmp.tickets.view', next.view);
    return next;
  });
  const setFilters = (patch: Partial<Filters>) => setConfig({ filters: { ...config.filters, ...patch } });
  const modified = !!selectedView && JSON.stringify(normalise(selectedView.config)) !== JSON.stringify(config);
  const chooseView = (v: SavedView | null) => {
    const next = new URLSearchParams(search);
    if (v) next.set('view', v.id); else next.delete('view');
    setSearch(next);
    if (!v) {            // "All tickets": no filters, no search; keep the layout and section
      const clean = normalise({ view: config.view, section: config.section });
      setConfigState(clean);
      storage.set(adhocKey, JSON.stringify(clean));
    }
  };
  const { view, query, mine, show_done: showAllDone, show_closed: showClosed, filters } = config;
  const setView = (v: View) => setConfig({ view: v });
  const setSectionFilter = (v: string) => setConfig({ section: v });
  const sectionFilter = config.section;
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const openNumber = Number(search.get('ticket')) || null;
  const open = (t: Ticket | null) => {
    const next = new URLSearchParams(search);
    if (t) next.set('ticket', String(t.number)); else next.delete('ticket');
    setSearch(next, { replace: !t });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),             // a tap still opens the ticket
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),   // press and hold to drag; swiping still scrolls
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const statusById = useMemo(() => Object.fromEntries((data?.statuses ?? []).map((s) => [s.id, s])), [data]);
  const doneIds = useMemo(() => new Set((data?.statuses ?? []).filter((s) => s.category === 'done').map((s) => s.id)), [data]);
  const openSections = (data?.sections ?? []).filter((s) => !s.closed_at);
  // The board's default: a sprint running today (it has dates and today is inside them), else everything open.
  const currentSprint = openSections.find((s) => s.kind === 'sprint' && !!s.starts_on && s.starts_on <= today() && (!s.ends_on || s.ends_on >= today()));
  const chosen = sectionFilter && (sectionFilter === 'all' || sectionFilter === 'none' || openSections.some((s) => s.id === sectionFilter)) ? sectionFilter : '';
  const boardSection = chosen || (currentSprint ? currentSprint.id : 'all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cutoff = new Date(Date.now() - RECENT_DONE_DAYS * 86400_000).toISOString();
    const closed = new Set((data?.sections ?? []).filter((s) => s.closed_at).map((s) => s.id));
    const f = filters, week = new Date(Date.now() + 7 * 86400_000).toISOString().slice(0, 10), now = today();
    return (data?.tickets ?? []).filter((t) =>
      (!q || t.title.toLowerCase().includes(q) || `#${t.number}` === q || String(t.number) === q) &&
      (!mine || t.assignees.some((a) => a.id === me?.id)) &&
      (showAllDone || !doneIds.has(t.status_id) || !t.completed_at || t.completed_at > cutoff) &&
      (showClosed || !t.section_id || !closed.has(t.section_id)) &&
      (!f.status.length || f.status.includes(t.status_id)) &&
      (!f.assignee.length || (f.assignee.includes('none') && !t.assignees.length) || t.assignees.some((a) => f.assignee.includes(a.id))) &&
      (!f.priority.length || f.priority.includes(t.priority)) &&
      (!f.kind.length || f.kind.includes(t.kind)) &&
      (!f.due || (f.due === 'none' ? !t.due_on : f.due === 'set' ? !!t.due_on
        : !!t.due_on && !doneIds.has(t.status_id) && (f.due === 'overdue' ? t.due_on < now : t.due_on >= now && t.due_on <= week))) &&
      (!f.client || t.created_by_client) &&
      (!f.internal || (f.internal === 'only' ? !!t.internal : !t.internal)));
  }, [data, query, mine, me, showAllDone, showClosed, doneIds, filters]);

  const onBoard = filtered.filter((t) => boardSection === 'all' || t.section_id === boardSection || (boardSection === 'none' && !t.section_id));

  const move = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status_id?: string; section_id?: string; to_no_section?: boolean; after_id?: string; before_id?: string }) =>
      post<Ticket>(`/api/tickets/${id}/move`, body),
    onError: (e: Error) => { toast(e.message, true); qc.invalidateQueries({ queryKey: ['board', project.id] }); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['board', project.id] }),
  });

  // Put the dragged ticket where it was dropped, at once, and tell the server.
  const drop = (id: string, group: { status?: string; section?: string | null }, list: Ticket[], index: number) => {
    const others = list.filter((t) => t.id !== id);
    const after = others[index - 1], before = others[index];
    qc.setQueryData<Board>(['board', project.id], (b) => {
      if (!b) return b;
      const lo = after?.rank, hi = before?.rank;
      const rank = lo === undefined && hi === undefined ? b.tickets.at(-1)!.rank + 1024 : lo === undefined ? hi! - 1024 : hi === undefined ? lo + 1024 : (lo + hi) / 2;
      const tickets = b.tickets.map((t) => t.id !== id ? t : {
        ...t, rank, ...(group.status ? { status_id: group.status } : {}), ...(group.section !== undefined ? { section_id: group.section } : {}),
      });
      return { ...b, tickets: tickets.sort((x, y) => x.rank - y.rank) };
    });
    move.mutate({ id, ...(group.status ? { status_id: group.status } : {}),
                  ...(group.section === null ? { to_no_section: true } : group.section ? { section_id: group.section } : {}),
                  after_id: after?.id, before_id: before?.id });
  };

  if (!data) return <span className="spin muted" />;
  const can = data.can;
  const active = activeId ? data.tickets.find((t) => t.id === activeId) : null;

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const id = String(e.active.id), over = e.over ? String(e.over.id) : null;
    if (!over || over === id) return;
    if (view === 'board') {
      const overTicket = data.tickets.find((t) => t.id === over);
      const status = overTicket ? overTicket.status_id : over.replace('status:', '');
      const column = onBoard.filter((t) => t.status_id === status);
      const index = overTicket ? column.filter((t) => t.id !== id).findIndex((t) => t.id === over) + (isBelow(e) ? 1 : 0) : column.length;
      drop(id, { status }, column, Math.max(0, index));
    } else {
      const overTicket = data.tickets.find((t) => t.id === over);
      const section = overTicket ? overTicket.section_id : (over === 'section:none' ? null : over.replace('section:', ''));
      const group = filtered.filter((t) => (t.section_id ?? null) === section);
      const index = overTicket ? group.filter((t) => t.id !== id).findIndex((t) => t.id === over) + (isBelow(e) ? 1 : 0) : group.length;
      drop(id, { section }, group, Math.max(0, index));
    }
  };

  const listSections: (TicketSection | null)[] = [...(showClosed ? data.sections : openSections)];
  if (filtered.some((t) => !t.section_id)) listSections.push(null);

  return (
    <div className="tickets">
      <div className="tickets-bar">
        <ViewsMenu project={project} views={views} selected={selectedView} modified={modified} config={config} onChoose={chooseView} />
        <div className="seg" role="group" aria-label="Layout">
          <button className={view === 'board' ? 'on' : ''} onClick={() => setView('board')} aria-pressed={view === 'board'} title="Board"><Icon name="board" className="ico sm" /><span className="hide-sm">Board</span></button>
          <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')} aria-pressed={view === 'list'} title="List"><Icon name="list" className="ico sm" /><span className="hide-sm">List</span></button>
        </div>
        {view === 'board' && (
          <select className="input compact" value={boardSection} onChange={(e) => setSectionFilter(e.target.value)} aria-label="Section">
            <option value="all">All sections</option>
            {openSections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            <option value="none">No section</option>
          </select>
        )}
        <input className="input compact search" placeholder="Find a ticket" value={query} onChange={(e) => setConfig({ query: e.target.value })} aria-label="Find a ticket" />
        <button className={`chip ${mine ? 'on' : ''}`} onClick={() => setConfig({ mine: !mine })} aria-pressed={mine}>Mine</button>
        <FilterButton board={data} filters={filters} onChange={setFilters} />
        <span className="grow" />
        <Menu label="View options" items={[
          { label: showAllDone ? `Only recent done tickets (${RECENT_DONE_DAYS} days)` : 'Show all done tickets', onClick: () => setConfig({ show_done: !showAllDone }) },
          { label: showClosed ? 'Hide finished sprints' : 'Show finished sprints', onClick: () => setConfig({ show_closed: !showClosed }) },
          ...(can.manage ? [{ label: 'Board settings…', onClick: () => setSettings(true) }] : []),
        ]} />
        {can.create && <button className="btn sm primary" onClick={() => setCreating(true)}><Icon name="plus" className="ico sm" /><span className="hide-sm">New ticket</span></button>}
      </div>
      <FilterChips board={data} filters={filters} onChange={setFilters} count={filtered.length} total={data.tickets.length} />
      {!can.triage && can.create && <p className="muted tiny" style={{ margin: '0 0 10px' }}>New tickets go into the backlog; Blue Market sorts and schedules them.</p>}

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
        {view === 'board' ? (
          <div className="board">
            {data.statuses.map((s) => {
              const column = onBoard.filter((t) => t.status_id === s.id);
              return (
                <DropZone key={s.id} id={`status:${s.id}`} className={`column cat-${s.category}`}>
                  <div className="column-head"><span className="column-name">{s.name}</span><span className="muted tiny">{column.length}</span></div>
                  <SortableContext items={column.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                    <div className="column-body">
                      {column.map((t) => <SortableTicket key={t.id} t={t} done={s.category === 'done'} onOpen={() => open(t)} disabled={!can.triage} />)}
                      {!column.length && <div className="column-empty">{s.category === 'done' && !showAllDone ? `Nothing finished in the last ${RECENT_DONE_DAYS} days` : 'Nothing here'}</div>}
                    </div>
                  </SortableContext>
                </DropZone>
              );
            })}
          </div>
        ) : (
          <div className="tlist">
            {listSections.map((sec) => <ListSection key={sec?.id ?? 'none'} section={sec} tickets={filtered.filter((t) => (t.section_id ?? null) === (sec?.id ?? null))}
                                                    statusById={statusById} doneIds={doneIds} canDrag={can.triage} onOpen={open} />)}
          </div>
        )}
        <DragOverlay>{active ? <TicketCard t={active} done={doneIds.has(active.status_id)} dragging /> : null}</DragOverlay>
      </DndContext>

      {creating && <NewTicket project={project} board={data} defaultSection={view === 'board' && boardSection !== 'all' && boardSection !== 'none' ? boardSection : undefined}
                              onClose={() => setCreating(false)} onCreated={(t) => { setCreating(false); open(t); }} />}
      {openNumber && <TicketPanel project={project} board={data} number={openNumber} onClose={() => open(null)} />}
      {settings && <BoardSettings project={project} board={data} onClose={() => setSettings(false)} />}
    </div>
  );
}

function isBelow(e: DragEndEvent): boolean {
  const a = e.active.rect.current.translated, o = e.over?.rect;
  return !!a && !!o && a.top + a.height / 2 > o.top + o.height / 2;
}

function ListSection({ section, tickets, statusById, doneIds, canDrag, onOpen }: {
  section: TicketSection | null; tickets: Ticket[]; statusById: Record<string, TicketStatus>; doneIds: Set<string>; canDrag: boolean; onOpen: (t: Ticket) => void;
}) {
  const key = `bmp.tickets.collapsed.${section?.id ?? 'none'}`;
  const [collapsed, setCollapsed] = useState(() => storage.get(key) === '1');
  const toggle = () => { setCollapsed(!collapsed); storage.set(key, collapsed ? '0' : '1'); };
  const open = tickets.filter((t) => !doneIds.has(t.status_id)).length;
  return (
    <DropZone id={`section:${section?.id ?? 'none'}`} className="tsection">
      <button className="tsection-head" onClick={toggle} aria-expanded={!collapsed}>
        <span className={`chev ${collapsed ? '' : 'open'}`}>›</span>
        <b>{section ? section.name : 'No section'}</b>
        {section?.kind === 'sprint' && (section.starts_on || section.ends_on) && <span className="muted tiny">{section.starts_on ? shortDate(section.starts_on) : '…'} – {section.ends_on ? shortDate(section.ends_on) : '…'}</span>}
        {section?.closed_at && <span className="badge tiny-badge">Finished</span>}
        <span className="grow" />
        <span className="muted tiny">{open} open{tickets.length > open ? ` · ${tickets.length - open} done` : ''}</span>
      </button>
      {!collapsed && (
        <SortableContext items={tickets.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tickets.map((t) => (
            <SortableTicket key={t.id} t={t} done={doneIds.has(t.status_id)} onOpen={() => onOpen(t)} disabled={!canDrag}>
              {(t) => {
                const st = statusById[t.status_id];
                return (
                  <div className={`trow ${doneIds.has(t.status_id) ? 'done' : ''}`} onClick={() => onOpen(t)} role="button" tabIndex={-1}>
                    <span className="num">#{t.number}</span>
                    <span className="trow-title">{t.kind === 'bug' && <Icon name="bug" className="ico xs bug" />}{t.title}{t.internal && <span className="tag-internal">Internal</span>}</span>
                    <PriorityMark p={t.priority} />
                    <Due on={t.due_on} done={doneIds.has(t.status_id)} />
                    <Avatars people={t.assignees} max={2} />
                    <span className={`status-pill cat-${st?.category}`}>{st?.name}</span>
                  </div>
                );
              }}
            </SortableTicket>
          ))}
          {!tickets.length && <div className="column-empty">No tickets</div>}
        </SortableContext>
      )}
    </DropZone>
  );
}

// ---------- create ----------

function NewTicket({ project, board, defaultSection, onClose, onCreated }: { project: Project; board: Board; defaultSection?: string; onClose: () => void; onCreated: (t: Ticket) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const staff = board.can.manage;
  const [f, setF] = useState({ title: '', description: '', kind: 'task' as TicketKind, priority: 'none' as TicketPriority,
                               section_id: defaultSection ?? board.sections.find((s) => s.kind === 'backlog')?.id ?? '', assignee_ids: [] as string[], internal: false });
  const [files, setFiles] = useState<Uploaded[]>([]);
  const create = useMutation({
    mutationFn: () => post<Ticket>(`/api/projects/${project.id}/tickets`, { ...f, section_id: f.section_id || null, attachment_ids: files.map((x) => x.id) }),
    onSuccess: (t) => { qc.invalidateQueries({ queryKey: ['board', project.id] }); onCreated(t); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title="New ticket" onClose={onClose} width={560}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (f.title.trim()) create.mutate(); }}>
        <label className="field"><span>Title</span>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={300} required autoFocus placeholder="What needs doing?" />
        </label>
        <div className="field"><span>Details <span className="muted">(optional; paste screenshots straight in)</span></span>
          <RichTextarea rows={4} value={f.description} onChange={(description) => setF((x) => ({ ...x, description }))} maxLength={50000}
                        uploadUrl={`/api/projects/${project.id}/attachments`} onFiles={setFiles} label="Details"
                        placeholder={staff ? '' : 'What happened, where, and what you expected. Screenshots help.'} />
        </div>
        <div className="grid-fields">
          <label className="field"><span>Kind</span>
            <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as TicketKind })}>
              {(Object.keys(KIND_LABEL) as TicketKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
          </label>
          {board.can.triage && <>
            <label className="field"><span>Priority</span>
              <select className="input" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value as TicketPriority })}>
                {(Object.keys(PRIORITY_LABEL) as TicketPriority[]).map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
              </select>
            </label>
            <label className="field"><span>Section</span>
              <select className="input" value={f.section_id} onChange={(e) => setF({ ...f, section_id: e.target.value })}>
                {board.sections.filter((s) => !s.closed_at).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                <option value="">No section</option>
              </select>
            </label>
          </>}
        </div>
        {staff && <>
          <PeoplePicker people={board.people} value={f.assignee_ids} onChange={(ids) => setF({ ...f, assignee_ids: ids })} />
          <label className="row small" style={{ gap: 8 }}><input type="checkbox" checked={f.internal} onChange={(e) => setF({ ...f, internal: e.target.checked })} />
            Internal (Blue Market only)</label>
        </>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!f.title.trim() || create.isPending || f.description.includes('![Uploading ')}>{create.isPending && <span className="spin" />}Create</button>
        </div>
      </form>
    </Modal>
  );
}

function PeoplePicker({ people, value, onChange, disabled }: { people: Person[]; value: string[]; onChange: (ids: string[]) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const chosen = people.filter((p) => value.includes(p.id));
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="field">
      <span>Assignees</span>
      <div className="picker">
        <button type="button" className="input picker-btn" onClick={() => setOpen(!open)} disabled={disabled} aria-expanded={open}>
          {chosen.length ? chosen.map((p) => p.name).join(', ') : <span className="muted">Nobody</span>}
        </button>
        {open && (
          <div className="picker-pop" role="listbox" aria-multiselectable>
            {people.map((p) => (
              <label key={p.id} className="picker-row"><input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} />
                <span className={`avatar xs ${p.is_staff ? 'staff' : ''}`}>{initials(p.name)}</span>{p.name}
                {!p.is_staff && <span className="muted tiny">client</span>}</label>
            ))}
            <div className="row" style={{ justifyContent: 'flex-end', padding: 6 }}><button type="button" className="btn sm" onClick={() => setOpen(false)}>Done</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- the ticket panel ----------

function TicketPanel({ project, board, number, onClose }: { project: Project; board: Board; number: number; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const summary = board.tickets.find((t) => t.number === number);
  const { data: t, error } = useQuery({ queryKey: ['ticket', summary?.id], queryFn: () => get<TicketDetail>(`/api/tickets/${summary!.id}`), enabled: !!summary });
  const [editing, setEditing] = useState<string | null>(null);
  const [descFiles, setDescFiles] = useState<Uploaded[]>([]);
  const [internalOpen, setInternalOpen] = useState(false);
  const [title, setTitle] = useState<string | null>(null);
  const { data: thread } = useQuery({ queryKey: ['ticket-comments', summary?.id], queryFn: () => get<TicketComments>(`/api/tickets/${summary!.id}/comments`), enabled: !!summary });
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape' && editing === null) onClose(); }; addEventListener('keydown', k); return () => removeEventListener('keydown', k); }, [editing, onClose]);
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => patch<TicketDetail>(`/api/tickets/${summary!.id}`, body),
    onSuccess: (d) => { qc.setQueryData(['ticket', summary!.id], (old: TicketDetail | undefined) => old ? { ...old, ...d } : old); qc.invalidateQueries({ queryKey: ['board', project.id] }); },
    onError: (e: Error) => toast(e.message, true),
  });
  const archive = async () => {
    if (!(await confirm(`Archive #${number}?`, { body: "It leaves the board and lists. Nothing is deleted.", ok: 'Archive', danger: true }))) return;
    try { await del(`/api/tickets/${summary!.id}`); qc.invalidateQueries({ queryKey: ['board', project.id] }); onClose(); } catch (e) { toast((e as Error).message, true); }
  };

  const shell = (body: ReactNode) => (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={`Ticket #${number}`}>{body}</aside>
    </>
  );
  if (!summary || error) return shell(<div className="drawer-body"><button className="btn sm ghost drawer-close" onClick={onClose} aria-label="Close"><Icon name="x" /></button><p className="muted">This ticket isn't on the board (it may be archived or internal).</p></div>);
  if (!t) return shell(<div className="drawer-body"><span className="spin muted" /></div>);
  const c = t.can;
  const status = t.statuses.find((s) => s.id === t.status_id);

  return shell(
    <div className="drawer-body">
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <span className="muted small mono">#{t.number}</span>
        {t.internal && <span className="tag-internal">Internal</span>}
        <span className="grow" />
        {c.manage && <Menu label="Ticket actions" items={[{ label: 'Archive', danger: true, onClick: archive }]} />}
        <button className="btn sm ghost" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
      </div>
      {t.can_edit_text ? (
        <textarea className="title-edit" rows={1} value={title ?? t.title} maxLength={300} aria-label="Title"
                  onChange={(e) => setTitle(e.target.value.replace(/\n/g, ''))}
                  onBlur={() => { if (title !== null && title.trim() && title.trim() !== t.title) save.mutate({ title: title.trim() }); setTitle(null); }} />
      ) : <h2 className="title-static">{t.title}</h2>}

      <div className="tfields">
        <Field label="Status">
          {c.triage ? <select className="input compact" value={t.status_id} onChange={(e) => save.mutate({ status_id: e.target.value })}>{t.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            : <span className={`status-pill cat-${status?.category}`}>{status?.name}</span>}
        </Field>
        <Field label="Section">
          {c.triage ? (
            <select className="input compact" value={t.section_id ?? ''} onChange={(e) => save.mutate(e.target.value ? { section_id: e.target.value } : { clear_section: true })}>
              {t.sections.filter((s) => !s.closed_at || s.id === t.section_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              <option value="">No section</option>
            </select>
          ) : <span className="small">{t.sections.find((s) => s.id === t.section_id)?.name ?? 'No section'}</span>}
        </Field>
        <Field label="Priority">
          {c.triage ? <select className="input compact" value={t.priority} onChange={(e) => save.mutate({ priority: e.target.value })}>{(Object.keys(PRIORITY_LABEL) as TicketPriority[]).map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}</select>
            : <span className="small">{PRIORITY_LABEL[t.priority]}</span>}
        </Field>
        <Field label="Kind">
          {c.manage ? <select className="input compact" value={t.kind} onChange={(e) => save.mutate({ kind: e.target.value })}>{(Object.keys(KIND_LABEL) as TicketKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select>
            : <span className="small">{KIND_LABEL[t.kind]}</span>}
        </Field>
        <Field label="Due">
          {c.manage ? <input type="date" className="input compact" value={t.due_on ?? ''} onChange={(e) => save.mutate(e.target.value ? { due_on: e.target.value } : { clear_due: true })} />
            : <span className="small">{t.due_on ? formatDate(t.due_on) : 'No date'}</span>}
        </Field>
        <Field label="Assignees" wide>
          {c.manage ? <PeoplePicker people={t.people} value={t.assignees.map((a) => a.id)} onChange={(ids) => save.mutate({ assignee_ids: ids })} />
            : <span className="small">{t.assignees.map((a) => a.name).join(', ') || 'Nobody yet'}</span>}
        </Field>
      </div>

      <div className="tdesc">
        <div className="row" style={{ alignItems: 'center' }}><b className="small grow">Details</b>
          {t.can_edit_text && editing === null && <button className="btn sm ghost" onClick={() => setEditing(t.description)}>{t.description ? 'Edit' : 'Add details'}</button>}</div>
        {editing !== null ? (
          <div className="stack" style={{ gap: 8 }}>
            <RichTextarea rows={8} value={editing} onChange={setEditing} maxLength={50000} autoFocus label="Details" onFiles={setDescFiles}
                          uploadUrl={`/api/projects/${project.id}/attachments`} onCancel={() => setEditing(null)} />
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn sm ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn sm primary" disabled={editing.includes('![Uploading ')}
                      onClick={() => { save.mutate({ description: editing, attachment_ids: descFiles.map((x) => x.id) }); setEditing(null); setDescFiles([]); }}>Save</button>
            </div>
          </div>
        ) : t.description_html ? <div className="prose small-prose rich-text" dangerouslySetInnerHTML={{ __html: t.description_html }} /> : <p className="muted small" style={{ margin: 0 }}>No details.</p>}
        {editing === null && <AttachmentList files={thread?.description_attachments ?? []} />}
      </div>

      <TicketDesignsSection ticketId={t.id} />
      {c.manage && (
        <details className="internal-box" open={internalOpen} onToggle={(e) => setInternalOpen((e.target as HTMLDetailsElement).open)}>
          <summary>
            <span className="eyebrow">Blue Market only</span>
            <span className="muted tiny internal-summary">
              {[t.internal ? 'Internal ticket' : null, t.estimate_hours != null ? `${t.estimate_hours} h` : null, t.cost_cents != null ? rand(t.cost_cents) : null].filter(Boolean).join(' · ') || 'Estimate, cost, visibility'}
            </span>
          </summary>
          <div className="tfields" style={{ marginTop: 10 }}>
            <Field label="Estimate (hours)"><NumberInput value={t.estimate_hours ?? null} onSave={(v) => save.mutate(v === null ? { clear_estimate: true } : { estimate_hours: v })} /></Field>
            <Field label="Cost (R)"><NumberInput value={t.cost_cents != null ? t.cost_cents / 100 : null} onSave={(v) => save.mutate(v === null ? { clear_cost: true } : { cost_cents: Math.round(v * 100) })} /></Field>
            <Field label="Internal ticket" wide>
              <label className="row small" style={{ gap: 8 }}><input type="checkbox" checked={!!t.internal} onChange={(e) => save.mutate({ internal: e.target.checked })} />Hide this ticket from the client</label>
            </Field>
          </div>
        </details>
      )}
      <p className="muted tiny" style={{ marginTop: 16 }}>Created by {t.created_by} · {formatDate(t.created_at)}{t.completed_at ? ` · finished ${formatDate(t.completed_at)}` : ''}</p>
      {thread && <TicketThread project={project} ticketId={t.id} thread={thread} />}
      {dialog}
    </div>,
  );
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return <div className={`tfield ${wide ? 'wide' : ''}`}><span className="tfield-label">{label}</span><div>{children}</div></div>;
}

function NumberInput({ value, onSave }: { value: number | null; onSave: (v: number | null) => void }) {
  const [v, setV] = useState(value === null ? '' : String(value));
  useEffect(() => setV(value === null ? '' : String(value)), [value]);
  const commit = () => { const n = v.trim() === '' ? null : Number(v.replace(',', '.')); if (n === null || (!Number.isNaN(n) && n >= 0)) { if (n !== value) onSave(n); } else setV(value === null ? '' : String(value)); };
  return <input className="input compact" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />;
}

// ---------- board settings (Blue Market) ----------

function BoardSettings({ project, board, onClose }: { project: Project; board: Board; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const set = (b: Board) => qc.setQueryData(['board', project.id], b);
  const run = async (p: Promise<Board>) => { try { set(await p); } catch (e) { toast((e as Error).message, true); } };
  const [newStatus, setNewStatus] = useState('');
  const [newSection, setNewSection] = useState({ name: '', kind: 'sprint' as 'sprint' | 'other', starts_on: '', ends_on: '' });
  const [removing, setRemoving] = useState<TicketStatus | null>(null);
  const [finishing, setFinishing] = useState<TicketSection | null>(null);
  const counts = useMemo(() => {
    const by: Record<string, number> = {};
    for (const t of board.tickets) { by[t.status_id] = (by[t.status_id] ?? 0) + 1; if (t.section_id) by[t.section_id] = (by[t.section_id] ?? 0) + 1; }
    return by;
  }, [board]);
  const moveItem = (ids: string[], i: number, d: number) => { const n = [...ids]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; };

  return (
    <Modal title="Board settings" onClose={onClose} width={640}>
      <div className="stack" style={{ gap: 18 }}>
        <section>
          <h3 className="settings-h">Statuses <span className="muted tiny">the board's columns, left to right</span></h3>
          {board.statuses.map((s, i) => (
            <div key={s.id} className="settings-row">
              <input className="input compact grow" defaultValue={s.name} maxLength={60} aria-label="Status name"
                     onBlur={(e) => e.target.value.trim() && e.target.value !== s.name && run(patch<Board>(`/api/ticket-statuses/${s.id}`, { name: e.target.value }))} />
              <select className="input compact" value={s.category} onChange={(e) => run(patch<Board>(`/api/ticket-statuses/${s.id}`, { category: e.target.value }))} aria-label="Means" title="What this status means: counts and 'done' dates follow it">
                <option value="todo">Not started</option><option value="doing">In progress</option><option value="done">Done</option>
              </select>
              <button className="btn sm ghost" disabled={i === 0} onClick={() => run(put<Board>(`/api/projects/${project.id}/ticket-statuses/order`, { ids: moveItem(board.statuses.map((x) => x.id), i, -1) }))} aria-label="Move left">↑</button>
              <button className="btn sm ghost" disabled={i === board.statuses.length - 1} onClick={() => run(put<Board>(`/api/projects/${project.id}/ticket-statuses/order`, { ids: moveItem(board.statuses.map((x) => x.id), i, 1) }))} aria-label="Move right">↓</button>
              <button className="btn sm ghost danger" disabled={board.statuses.length < 2} onClick={() => setRemoving(s)} aria-label={`Remove ${s.name}`}><Icon name="x" className="ico sm" /></button>
            </div>
          ))}
          <form className="settings-row" onSubmit={(e) => { e.preventDefault(); if (newStatus.trim()) { run(post<Board>(`/api/projects/${project.id}/ticket-statuses`, { name: newStatus, category: 'doing' })); setNewStatus(''); } }}>
            <input className="input compact grow" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} placeholder="New status, e.g. Blocked" maxLength={60} />
            <button className="btn sm" disabled={!newStatus.trim()}>Add</button>
          </form>
        </section>
        <section>
          <h3 className="settings-h">Sections and sprints <span className="muted tiny">how the list is grouped</span></h3>
          {board.sections.filter((s) => !s.closed_at).map((s, i, open) => (
            <div key={s.id} className="settings-row">
              <input className="input compact grow" defaultValue={s.name} maxLength={80} aria-label="Section name"
                     onBlur={(e) => e.target.value.trim() && e.target.value !== s.name && run(patch<Board>(`/api/ticket-sections/${s.id}`, { name: e.target.value }))} />
              {s.kind === 'sprint' && <>
                <input type="date" className="input compact" defaultValue={s.starts_on ?? ''} aria-label="Starts" onBlur={(e) => e.target.value !== (s.starts_on ?? '') && run(patch<Board>(`/api/ticket-sections/${s.id}`, { starts_on: e.target.value || null }))} />
                <input type="date" className="input compact" defaultValue={s.ends_on ?? ''} aria-label="Ends" onBlur={(e) => e.target.value !== (s.ends_on ?? '') && run(patch<Board>(`/api/ticket-sections/${s.id}`, { ends_on: e.target.value || null }))} />
              </>}
              {s.kind === 'backlog' && <span className="muted tiny">Backlog</span>}
              <button className="btn sm ghost" disabled={i === 0} onClick={() => run(put<Board>(`/api/projects/${project.id}/ticket-sections/order`, { ids: [...moveItem(open.map((x) => x.id), i, -1), ...board.sections.filter((x) => x.closed_at).map((x) => x.id)] }))} aria-label="Move up">↑</button>
              <button className="btn sm ghost" disabled={i === open.length - 1} onClick={() => run(put<Board>(`/api/projects/${project.id}/ticket-sections/order`, { ids: [...moveItem(open.map((x) => x.id), i, 1), ...board.sections.filter((x) => x.closed_at).map((x) => x.id)] }))} aria-label="Move down">↓</button>
              {s.kind === 'sprint' ? <button className="btn sm" onClick={() => setFinishing(s)}>Finish</button>
                : s.kind !== 'backlog' && <button className="btn sm ghost danger" aria-label={`Remove ${s.name}`} onClick={async () => {
                  if (await confirm(`Remove ${s.name}?`, { body: `Its ${counts[s.id] ?? 0} tickets are kept, with no section.`, ok: 'Remove', danger: true })) run(del<Board>(`/api/ticket-sections/${s.id}`));
                }}><Icon name="x" className="ico sm" /></button>}
            </div>
          ))}
          <form className="settings-row wrap" onSubmit={(e) => { e.preventDefault(); if (newSection.name.trim()) { run(post<Board>(`/api/projects/${project.id}/ticket-sections`, { ...newSection, starts_on: newSection.starts_on || null, ends_on: newSection.ends_on || null })); setNewSection({ ...newSection, name: '', starts_on: '', ends_on: '' }); } }}>
            <select className="input compact" value={newSection.kind} onChange={(e) => setNewSection({ ...newSection, kind: e.target.value as 'sprint' | 'other' })} aria-label="Kind">
              <option value="sprint">Sprint</option><option value="other">Section</option>
            </select>
            <input className="input compact grow" value={newSection.name} onChange={(e) => setNewSection({ ...newSection, name: e.target.value })} placeholder={newSection.kind === 'sprint' ? `Sprint ${board.sections.filter((s) => s.kind === 'sprint').length + 1}` : 'e.g. Launch, Later'} maxLength={80} />
            {newSection.kind === 'sprint' && <>
              <input type="date" className="input compact" value={newSection.starts_on} onChange={(e) => setNewSection({ ...newSection, starts_on: e.target.value })} aria-label="Starts" />
              <input type="date" className="input compact" value={newSection.ends_on} onChange={(e) => setNewSection({ ...newSection, ends_on: e.target.value })} aria-label="Ends" />
            </>}
            <button className="btn sm" disabled={!newSection.name.trim()}>Add</button>
          </form>
          {board.sections.some((s) => s.closed_at) && <p className="muted tiny" style={{ margin: '6px 0 0' }}>{board.sections.filter((s) => s.closed_at).length} finished sprint(s) are kept as a record; show them from the list's view options.</p>}
        </section>
      </div>
      {removing && (
        <PickModal title={`Remove ${removing.name}?`} body={`Its ${counts[removing.id] ?? 0} tickets move to:`} ok="Remove" danger
                   options={board.statuses.filter((s) => s.id !== removing.id).map((s) => ({ value: s.id, label: s.name }))}
                   onClose={() => setRemoving(null)} onPick={(v) => { run(del<Board>(`/api/ticket-statuses/${removing.id}`, { move_to: v })); setRemoving(null); }} />
      )}
      {finishing && (
        <PickModal title={`Finish ${finishing.name}?`} body="Done tickets stay with it as a record. Unfinished ones move to:" ok="Finish sprint"
                   options={[...board.sections.filter((s) => !s.closed_at && s.id !== finishing.id).map((s) => ({ value: s.id, label: s.name })), { value: '', label: 'No section' }]}
                   onClose={() => setFinishing(null)} onPick={(v) => { run(post<Board>(`/api/ticket-sections/${finishing.id}/close`, { move_open_to: v || null })); setFinishing(null); }} />
      )}
      {dialog}
    </Modal>
  );
}

function PickModal({ title, body, ok, danger, options, onClose, onPick }: { title: string; body: string; ok: string; danger?: boolean; options: { value: string; label: string }[]; onClose: () => void; onPick: (v: string) => void }) {
  const [v, setV] = useState(options[0]?.value ?? '');
  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack">
        <label className="field"><span>{body}</span>
          <select className="input" value={v} onChange={(e) => setV(e.target.value)}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className={`btn ${danger ? 'danger' : 'primary'}`} onClick={() => onPick(v)}>{ok}</button>
        </div>
      </div>
    </Modal>
  );
}


// ---------- comments on a ticket ----------

function TicketThread({ project, ticketId, thread }: { project: Project; ticketId: string; thread: TicketComments }) {
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ['ticket-comments', ticketId] }); qc.invalidateQueries({ queryKey: ['board', project.id] }); };
  const [text, setText] = useState('');
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState<Uploaded[]>([]);
  const [mentions, setMentions] = useState<string[]>([]);
  const box = useRef<RichTextareaHandle>(null);
  const people = internal ? thread.mentionable.filter((p) => p.is_staff) : thread.mentionable;
  const send = useMutation({
    mutationFn: () => post<TicketComment>(`/api/tickets/${ticketId}/comments`, { body: text, internal, mention_ids: mentions, attachment_ids: files.map((f) => f.id) }),
    onSuccess: () => { box.current?.reset(); setInternal(false); refresh(); },
    onError: (e: Error) => toast(e.message, true),
  });
  const uploading = text.includes('![Uploading ');
  return (
    <section className="tthread">
      <b className="small">Comments{thread.comments.length ? ` (${thread.comments.filter((c) => !c.deleted).length})` : ''}</b>
      {thread.comments.filter((c) => !c.deleted).map((c) => <TicketCommentItem key={c.id} c={c} project={project} thread={thread} onChange={refresh} />)}
      {thread.can_comment && (
        <form className={`composer ${internal ? 'internal' : ''}`} onSubmit={(e) => { e.preventDefault(); if (text.trim() && !uploading) send.mutate(); }}>
          <RichTextarea ref={box} value={text} onChange={setText} rows={text.length > 80 ? 4 : 2} people={people} onMentions={setMentions} onFiles={setFiles}
                        uploadUrl={`/api/projects/${project.id}/attachments`} placeholder="Comment, @mention someone, or paste a screenshot"
                        onSubmit={() => text.trim() && !uploading && send.mutate()} />
          <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
            {thread.can_comment_internal
              ? <label className="row tiny" title="Only Blue Market sees internal comments"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />Internal</label>
              : <span />}
            <button className="btn sm primary" disabled={!text.trim() || send.isPending || uploading}>{send.isPending && <span className="spin" />}Comment</button>
          </div>
        </form>
      )}
    </section>
  );
}

function TicketCommentItem({ c, project, thread, onChange }: { c: TicketComment; project: Project; thread: TicketComments; onChange: () => void }) {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<string | null>(null);
  const [files, setFiles] = useState<Uploaded[]>([]);
  const [mentions, setMentions] = useState<string[]>([]);
  const save = async () => {
    try { await patch(`/api/ticket-comments/${c.id}`, { body: editing, mention_ids: mentions, attachment_ids: files.map((f) => f.id) }); setEditing(null); onChange(); }
    catch (e) { toast((e as Error).message, true); }
  };
  const remove = async () => {
    if (!(await confirm('Delete this comment?', { body: 'Its attachments go with it.', ok: 'Delete', danger: true }))) return;
    try { await del(`/api/ticket-comments/${c.id}`); onChange(); } catch (e) { toast((e as Error).message, true); }
  };
  const items = [...(c.can_edit ? [{ label: 'Edit', onClick: () => setEditing(c.body) }] : []), ...(c.can_delete ? [{ label: 'Delete', danger: true, onClick: remove }] : [])];
  return (
    <div className={`comment tcomment ${c.internal ? 'internal' : ''}`}>
      <div className={`avatar sm ${c.author.is_staff ? 'staff' : ''}`} aria-hidden="true">{initials(c.author.name)}</div>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 4, alignItems: 'flex-start' }}>
          <div className="row wrap grow" style={{ gap: '2px 6px', minHeight: 26 }}>
            <b className="small">{c.author.name}</b>
            {c.internal && <span className="tag-internal" title="Only Blue Market sees this">Internal</span>}
            <span className="muted tiny" title={new Date(c.created_at).toLocaleString('en-ZA')}>{ago(c.created_at)}{c.edited ? ' · edited' : ''}</span>
          </div>
          {items.length > 0 && editing === null && <Menu label="Comment actions" items={items} />}
        </div>
        {editing !== null ? (
          <div className="stack" style={{ gap: 6 }}>
            <RichTextarea value={editing} onChange={setEditing} rows={3} autoFocus people={c.internal ? thread.mentionable.filter((p) => p.is_staff) : thread.mentionable}
                          onMentions={setMentions} onFiles={setFiles} uploadUrl={`/api/projects/${project.id}/attachments`} onSubmit={save} onCancel={() => setEditing(null)} />
            <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
              <button className="btn sm ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn sm primary" onClick={save} disabled={!editing.trim() || editing.includes('![Uploading ')}>Save</button>
            </div>
          </div>
        ) : <>
          <div className="comment-text rich-text" dangerouslySetInnerHTML={{ __html: c.body_html }} />
          <AttachmentList files={c.attachments} />
        </>}
      </div>
      {dialog}
    </div>
  );
}


// ---------- linked design screens ----------

function TicketDesignsSection({ ticketId }: { ticketId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ticket-designs', ticketId], queryFn: () => get<TicketDesigns>(`/api/tickets/${ticketId}/designs`) });
  const [picking, setPicking] = useState(false);
  const set = (designs: TicketDesign[]) => qc.setQueryData(['ticket-designs', ticketId], (old: TicketDesigns | undefined) => old ? { ...old, designs } : old);
  const unlink = async (d: TicketDesign) => {
    try { set(await del<TicketDesign[]>(`/api/ticket-designs/${d.id}`)); qc.invalidateQueries({ queryKey: ['round-tickets'] }); } catch (e) { toast((e as Error).message, true); }
  };
  if (!data || (!data.designs.length && !data.can_link)) return null;
  return (
    <div className="tdesigns">
      <div className="row" style={{ alignItems: 'center' }}>
        <b className="small grow">Designs</b>
        {data.can_link && data.choices.length > 0 && <button className="btn sm ghost" onClick={() => setPicking(true)}>Link a screen</button>}
      </div>
      {data.designs.length > 0 ? (
        <div className="design-links">
          {data.designs.map((d) => (
            <div key={d.id} className="design-link">
              <Link to={d.viewer_url} className="design-thumb" title={`Open v${d.round} · ${d.title}`}>
                {d.screen_url && d.frame ? <Frame src={d.screen_url} frame={d.frame} scale={d.frame === 'desktop' ? 0.075 : 0.16} title={d.title} lazy />
                  : <span className="design-round">v{d.round}</span>}
              </Link>
              <div className="design-meta">
                <Link to={d.viewer_url} className="small ellipsis">{d.title}</Link>
                <span className="muted tiny">v{d.round}{d.from_comment ? ' · from a comment' : ''}</span>
                {d.newer_url && <Link to={d.newer_url} className="tiny">Newer in v{d.newer} →</Link>}
              </div>
              {data.can_link && <button className="design-unlink" onClick={() => unlink(d)} aria-label={`Unlink ${d.title}`} title="Unlink">×</button>}
            </div>
          ))}
        </div>
      ) : <p className="muted small" style={{ margin: 0 }}>Not linked to a design yet.</p>}
      {picking && <DesignPicker ticketId={ticketId} data={data} onClose={() => setPicking(false)} onLinked={(designs) => { set(designs); setPicking(false); qc.invalidateQueries({ queryKey: ['round-tickets'] }); }} />}
    </div>
  );
}

function DesignPicker({ ticketId, data, onClose, onLinked }: { ticketId: string; data: TicketDesigns; onClose: () => void; onLinked: (d: TicketDesign[]) => void }) {
  const toast = useToast();
  const [round, setRound] = useState(data.choices[0]?.round);
  const [q, setQ] = useState('');
  const choice = data.choices.find((c) => c.round === round);
  const linked = new Set(data.designs.filter((d) => d.round === round).map((d) => d.screen_id ?? ''));
  const screens = (choice?.screens ?? []).filter((s) => !q.trim() || s.title.toLowerCase().includes(q.toLowerCase()) || s.id.includes(q.toLowerCase()));
  const link = async (screen_id: string | null) => {
    try { onLinked(await post<TicketDesign[]>(`/api/tickets/${ticketId}/designs`, { round, screen_id })); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title="Link a design screen" onClose={onClose} width={520}>
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 8 }}>
          <select className="input compact" value={round} onChange={(e) => setRound(Number(e.target.value))} aria-label="Round">
            {data.choices.map((c) => <option key={c.round} value={c.round}>v{c.round}{c.visibility === 'internal' ? ' · internal' : ''}</option>)}
          </select>
          <input className="input compact grow" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a screen" autoFocus aria-label="Find a screen" />
        </div>
        <div className="screen-pick">
          <button className="screen-pick-row" disabled={linked.has('')} onClick={() => link(null)}><span className="muted">The whole round</span>{linked.has('') && <span className="muted tiny">linked</span>}</button>
          {screens.map((s, i) => (
            <button key={s.id} className="screen-pick-row" disabled={linked.has(s.id)} onClick={() => link(s.id)}>
              <span className="num">{i + 1}</span><span className="grow">{s.title}</span>{linked.has(s.id) && <span className="muted tiny">linked</span>}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ---------- views and filters ----------

const NO_FILTERS: Filters = { status: [], assignee: [], priority: [], kind: [], due: '', client: false, internal: '' };
const DUE_LABEL: Record<Filters['due'], string> = { '': 'Any', overdue: 'Overdue', week: 'Due in the next 7 days', none: 'No due date', set: 'Has a due date' };

function normalise(c: Partial<ViewConfig> & { filters?: Partial<Filters> }): ViewConfig {
  return { view: c.view === 'list' ? 'list' : 'board', section: c.section ?? '', query: c.query ?? '', mine: !!c.mine,
           show_done: !!c.show_done, show_closed: !!c.show_closed, filters: { ...NO_FILTERS, ...(c.filters ?? {}) } };
}

const activeCount = (f: Filters) => f.status.length + f.assignee.length + f.priority.length + f.kind.length + (f.due ? 1 : 0) + (f.client ? 1 : 0) + (f.internal ? 1 : 0);

function ViewsMenu({ project, views, selected, modified, config, onChoose }: {
  project: Project; views: SavedView[]; selected: SavedView | null; modified: boolean; config: ViewConfig; onChoose: (v: SavedView | null) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { me } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState<null | 'new' | 'rename'>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['views', project.id] });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest('.views')) setOpen(false); };
    addEventListener('mousedown', close);
    return () => removeEventListener('mousedown', close);
  }, [open]);
  const act = async (fn: () => Promise<unknown>, done?: string) => { setOpen(false); try { await fn(); refresh(); if (done) toast(done); } catch (e) { toast((e as Error).message, true); } };
  const personal = views.filter((v) => v.mine && !v.shared), shared = views.filter((v) => v.shared);
  const side = me?.is_staff ? 'Blue Market' : (me?.client?.name ?? 'your team');
  return (
    <div className="views">
      <button className={`btn sm views-btn ${selected ? 'chosen' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open} title="Saved views">
        <span className="ellipsis">{selected ? selected.name : 'All tickets'}</span>{modified && <span className="modified" title="Changed since it was saved">•</span>}<span className="caret">▾</span>
      </button>
      {open && (
        <div className="views-pop" role="menu">
          <button className={!selected ? 'on' : ''} onClick={() => { setOpen(false); onChoose(null); }}>All tickets</button>
          {personal.length > 0 && <div className="views-label">Your views</div>}
          {personal.map((v) => <button key={v.id} className={selected?.id === v.id ? 'on' : ''} onClick={() => { setOpen(false); onChoose(v); }}>{v.name}</button>)}
          {shared.length > 0 && <div className="views-label">Shared with {side}</div>}
          {shared.map((v) => <button key={v.id} className={selected?.id === v.id ? 'on' : ''} onClick={() => { setOpen(false); onChoose(v); }}>
            {v.name}{!v.mine && <span className="muted tiny">{v.owner}</span>}</button>)}
          <div className="views-sep" />
          {selected?.mine && modified && <button onClick={() => act(() => patch(`/api/views/${selected.id}`, { config }), `Saved "${selected.name}"`)}>Save changes to "{selected.name}"</button>}
          <button onClick={() => { setOpen(false); setSaving('new'); }}>Save as a new view…</button>
          {selected && <>
            <button onClick={() => act(() => navigator.clipboard.writeText(`${location.origin}/projects/${project.id}?tab=tickets&view=${selected.id}`), 'Link copied')}>Copy link to this view</button>
            {selected.mine && <button onClick={() => { setOpen(false); setSaving('rename'); }}>Rename…</button>}
            {selected.mine && <button onClick={() => act(() => patch(`/api/views/${selected.id}`, { shared: !selected.shared }), selected.shared ? 'Now only yours' : `Shared with ${side}`)}>
              {selected.shared ? 'Make it only yours' : `Share with ${side}`}</button>}
            {(selected.mine || (selected.shared && me?.is_staff)) && <button className="danger" onClick={async () => {
              setOpen(false);
              if (await confirm(`Remove the view "${selected.name}"?`, { body: selected.shared ? 'It disappears for everyone it was shared with. Tickets are not affected.' : 'Tickets are not affected.', ok: 'Remove', danger: true })) {
                try { await del(`/api/views/${selected.id}`); onChoose(null); refresh(); } catch (e) { toast((e as Error).message, true); }
              }
            }}>Remove view</button>}
          </>}
        </div>
      )}
      {saving && <SaveViewDialog mode={saving} side={side} initial={saving === 'rename' ? selected!.name : ''}
                                 onClose={() => setSaving(null)} onSave={async (name, share) => {
        try {
          if (saving === 'rename') await patch(`/api/views/${selected!.id}`, { name });
          else { const v = await post<SavedView>(`/api/projects/${project.id}/views`, { name, shared: share, config }); onChoose(v); }
          setSaving(null); refresh(); toast(saving === 'rename' ? 'Renamed' : `Saved "${name}"`);
        } catch (e) { toast((e as Error).message, true); }
      }} />}
      {dialog}
    </div>
  );
}

function SaveViewDialog({ mode, side, initial, onClose, onSave }: { mode: 'new' | 'rename'; side: string; initial: string; onClose: () => void; onSave: (name: string, shared: boolean) => void }) {
  const [name, setName] = useState(initial);
  const [shared, setShared] = useState(false);
  return (
    <Modal title={mode === 'new' ? 'Save this view' : 'Rename view'} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSave(name.trim(), shared); }}>
        <label className="field"><span>Name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus required placeholder="e.g. Urgent bugs" /></label>
        {mode === 'new' && <>
          <p className="muted tiny" style={{ margin: 0 }}>Saves the layout, section, search and filters you have now.</p>
          <label className="row small" style={{ gap: 8 }}><input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />Share with {side}</label>
        </>}
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!name.trim()}>Save</button>
        </div>
      </form>
    </Modal>
  );
}

function FilterButton({ board, filters, onChange }: { board: Board; filters: Filters; onChange: (f: Partial<Filters>) => void }) {
  const [open, setOpen] = useState(false);
  const { me } = useAuth();
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest('.filter')) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    addEventListener('mousedown', close); addEventListener('keydown', esc);
    return () => { removeEventListener('mousedown', close); removeEventListener('keydown', esc); };
  }, [open]);
  const n = activeCount(filters);
  const toggle = <K extends 'status' | 'assignee' | 'priority' | 'kind'>(key: K, value: Filters[K][number]) => {
    const list = filters[key] as string[];
    onChange({ [key]: list.includes(value as string) ? list.filter((x) => x !== value) : [...list, value] } as Partial<Filters>);
  };
  // Assignees: the people on this board's tickets (clients don't get the full list of Blue Market's people).
  const people = useMemo(() => {
    const seen = new Map<string, Person>();
    for (const p of board.people) seen.set(p.id, p);
    for (const t of board.tickets) for (const a of t.assignees) seen.set(a.id, a);
    return [...seen.values()].sort((a, b) => Number(!a.is_staff) - Number(!b.is_staff) || a.name.localeCompare(b.name));
  }, [board]);
  return (
    <div className="filter">
      <button className={`chip ${n ? 'on' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open}>Filter{n ? ` · ${n}` : ''}</button>
      {open && (
        <div className="filter-pop" role="dialog" aria-label="Filter tickets">
          <FilterGroup label="Status">{board.statuses.map((s) => <Check key={s.id} on={filters.status.includes(s.id)} onClick={() => toggle('status', s.id)}>{s.name}</Check>)}</FilterGroup>
          <FilterGroup label="Assignee">
            <Check on={filters.assignee.includes('none')} onClick={() => toggle('assignee', 'none')}>Nobody</Check>
            {me && <Check on={filters.assignee.includes(me.id)} onClick={() => toggle('assignee', me.id)}>Me</Check>}
            {people.filter((p) => p.id !== me?.id).map((p) => <Check key={p.id} on={filters.assignee.includes(p.id)} onClick={() => toggle('assignee', p.id)}>{p.name}</Check>)}
          </FilterGroup>
          <FilterGroup label="Priority">{(['urgent', 'high', 'medium', 'low', 'none'] as TicketPriority[]).map((p) => <Check key={p} on={filters.priority.includes(p)} onClick={() => toggle('priority', p)}>{PRIORITY_LABEL[p]}</Check>)}</FilterGroup>
          <FilterGroup label="Kind">{(Object.keys(KIND_LABEL) as TicketKind[]).map((k) => <Check key={k} on={filters.kind.includes(k)} onClick={() => toggle('kind', k)}>{KIND_LABEL[k]}</Check>)}</FilterGroup>
          <FilterGroup label="Due">{(Object.keys(DUE_LABEL) as Filters['due'][]).map((d) => <Check key={d || 'any'} radio on={filters.due === d} onClick={() => onChange({ due: d })}>{DUE_LABEL[d]}</Check>)}</FilterGroup>
          <FilterGroup label="Other">
            <Check on={filters.client} onClick={() => onChange({ client: !filters.client })}>{me?.is_staff ? 'Raised by the client' : 'Raised by us'}</Check>
            {board.can.manage && <>
              <Check on={filters.internal === 'only'} onClick={() => onChange({ internal: filters.internal === 'only' ? '' : 'only' })}>Only internal tickets</Check>
              <Check on={filters.internal === 'hide'} onClick={() => onChange({ internal: filters.internal === 'hide' ? '' : 'hide' })}>Hide internal tickets</Check>
            </>}
          </FilterGroup>
          <div className="filter-foot">
            <button className="link-btn small" disabled={!n} onClick={() => onChange(NO_FILTERS)}>Clear filters</button>
            <button className="btn sm" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

const FilterGroup = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="filter-group"><div className="filter-label">{label}</div><div className="filter-options">{children}</div></div>
);

const Check = ({ on, onClick, radio, children }: { on: boolean; onClick: () => void; radio?: boolean; children: ReactNode }) => (
  <button type="button" className={`fopt ${on ? 'on' : ''} ${radio ? 'radio' : ''}`} onClick={onClick} aria-pressed={on}>{children}</button>
);

/** What's filtered, as small chips that each remove one filter; only shown when something is. */
function FilterChips({ board, filters, onChange, count, total }: { board: Board; filters: Filters; onChange: (f: Partial<Filters>) => void; count: number; total: number }) {
  const { me } = useAuth();
  if (!activeCount(filters)) return null;
  const name = (id: string) => id === 'none' ? 'Nobody' : id === me?.id ? 'Me' : (board.people.find((p) => p.id === id) ?? board.tickets.flatMap((t) => t.assignees).find((a) => a.id === id))?.name ?? 'Someone';
  const chips: { label: string; clear: () => void }[] = [
    ...filters.status.map((id) => ({ label: board.statuses.find((s) => s.id === id)?.name ?? 'Status', clear: () => onChange({ status: filters.status.filter((x) => x !== id) }) })),
    ...filters.assignee.map((id) => ({ label: name(id), clear: () => onChange({ assignee: filters.assignee.filter((x) => x !== id) }) })),
    ...filters.priority.map((p) => ({ label: PRIORITY_LABEL[p], clear: () => onChange({ priority: filters.priority.filter((x) => x !== p) }) })),
    ...filters.kind.map((k) => ({ label: KIND_LABEL[k], clear: () => onChange({ kind: filters.kind.filter((x) => x !== k) }) })),
    ...(filters.due ? [{ label: DUE_LABEL[filters.due], clear: () => onChange({ due: '' }) }] : []),
    ...(filters.client ? [{ label: me?.is_staff ? 'Raised by the client' : 'Raised by us', clear: () => onChange({ client: false }) }] : []),
    ...(filters.internal ? [{ label: filters.internal === 'only' ? 'Only internal' : 'No internal', clear: () => onChange({ internal: '' }) }] : []),
  ];
  return (
    <div className="filter-chips">
      {chips.map((c, i) => <span key={i} className="fchip">{c.label}<button onClick={c.clear} aria-label={`Remove filter ${c.label}`}>×</button></span>)}
      <span className="muted tiny">{count} of {total}</span>
      <button className="link-btn tiny" onClick={() => onChange(NO_FILTERS)}>Clear</button>
    </div>
  );
}
